import { AxiosError } from 'axios';
import { axiosClient, getUserVar, setUserVar, USER_VARIABLE } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { UserVar } from '@nx-frontend/shared/types';
import { Workspace } from '@nx-frontend/shared/types';
import { GetWorkspaceOptions } from '@nx-frontend/shared/types';
import { WorkspaceAvatar } from '@nx-frontend/shared/types';
import { GetWorkspacesAvatarsParams } from '@nx-frontend/shared/types';
import { SetLatestWorkspaceIdOptions } from '@nx-frontend/shared/types';
import { GetLatestWorkspaceIdOptions } from '@nx-frontend/shared/types';
import { GetLatestWorkspaceIdByUserVarsOptions } from '@nx-frontend/shared/types';

export async function getWorkspaces(options: GetWorkspaceOptions): Promise<Workspace[]> {
  const { userId } = options;
  let workspaces: Workspace[] = [];

  try {
    const queryParams: Record<string, unknown> = {};
    if (options.orgId) {
      queryParams.org = options.orgId;
    }
    const response = await axiosClient.get<Workspace[]>(`${generalEnv.workspaceServiceUrl}/v1/workspaces`, {
      headers: {
        Authorization: `internal ${userId}:auto`,
      },
      params: queryParams,
    });
    workspaces = response.data;
  } catch (err) {
    console.error(`getWorkspaces error: ${err}`);
  }

  // sort workspaces by createdAt ascending
  workspaces.sort((a, b) => {
    if (a.createdAt < b.createdAt) {
      return -1;
    }
    if (a.createdAt > b.createdAt) {
      return 1;
    }
    return 0;
  });

  return workspaces;
}

export async function getWorkspaceAvatar(
  workspaceId: string,
  options: { userId: number }
): Promise<WorkspaceAvatar | null> {
  const { userId } = options;
  try {
    const response = await axiosClient.get<WorkspaceAvatar>(
      `${generalEnv.workspaceServiceUrl}/v1/workspaces/${workspaceId}/avatar`,
      {
        headers: {
          Authorization: `internal ${userId}:auto`,
        },
      }
    );
    if (response.data) {
      return response.data as WorkspaceAvatar;
    }
  } catch (err) {
    console.error(`getWorkspaceAvatar error: ${err}`);
  }
  return null;
}

export async function getWorkspacesAvatars(
  { workspaces }: GetWorkspacesAvatarsParams,
  options: GetWorkspaceOptions
): Promise<WorkspaceAvatar[]> {
  const { userId } = options;
  let workspacesAvatars: WorkspaceAvatar[] = [];
  if (!workspaces.length) {
    return [];
  }

  try {
    const response = await axiosClient.get<WorkspaceAvatar[]>(
      `${generalEnv.workspaceServiceUrl}/v1/avatars?filter={"workspaceId": [${workspaces.map(
        (workspace) => `"${workspace.globalId}"`
      )}]}`,
      {
        headers: {
          Authorization: `internal ${userId}:auto`,
        },
      }
    );
    workspacesAvatars = response.data;
  } catch (err) {
    console.error(`getWorkspacesAvatars error: ${err}`);
  }

  return workspacesAvatars;
}

export const getLatestWorkspaceId = async (options: GetLatestWorkspaceIdOptions): Promise<string | null> => {
  const latestWorkspaceIdMap = await getLatestWorkspaceIdMap({ userId: options.userId });
  if (!latestWorkspaceIdMap) {
    return null;
  }

  if (typeof latestWorkspaceIdMap[options.orgId] === 'undefined') {
    return null;
  }

  return latestWorkspaceIdMap[options.orgId] || null;
};

export const getLatestOrgId = async ({ userId }: { userId: number }): Promise<string | null> => {
  let latestOrgResponse: UserVar;
  try {
    latestOrgResponse = await getUserVar({
      host: generalEnv.userServiceUrl,
      userId: userId,
      key: USER_VARIABLE.LAST_OPENED_ORG,
    });
  } catch (err) {
    if (err instanceof AxiosError) {
      const { response } = err;
      if (response?.status === 404) {
        // no variable found on the server
        return null;
      } else {
        throw err;
      }
    } else {
      throw err;
    }
  }

  if (!latestOrgResponse) {
    return null;
  }

  return (latestOrgResponse.value as string) || null;
};

export const setLatestWorkspaceId = async (options: SetLatestWorkspaceIdOptions): Promise<string | null> => {
  let latestWorkspaceIdMap = await getLatestWorkspaceIdMap({ userId: options.userId });
  if (!latestWorkspaceIdMap) {
    latestWorkspaceIdMap = {};
  }

  latestWorkspaceIdMap[options.orgId] = options.workspaceId;
  try {
    await setUserVar({
      host: generalEnv.userServiceUrl,
      userId: options.userId,
      key: USER_VARIABLE.LAST_OPENED_ORG,
      value: options.orgId,
    });
  } catch (err) {
    //do nothing
  }
  const response = await setUserVar({
    host: generalEnv.userServiceUrl,
    userId: options.userId,
    key: USER_VARIABLE.LAST_OPENED_WORKSPACES,
    value: latestWorkspaceIdMap,
  });

  if (!response.value) {
    return null;
  }

  latestWorkspaceIdMap = (response.value as { [key: string]: string }) || null;
  if (!latestWorkspaceIdMap) {
    return null;
  }

  if (typeof latestWorkspaceIdMap[options.orgId] === 'undefined') {
    return null;
  }

  return latestWorkspaceIdMap[options.orgId] || null;
};

export const getLatestWorkspaceIdMap = async (
  options: GetWorkspaceOptions
): Promise<{ [key: string]: string } | null> => {
  let latestWorkspaceResponse: UserVar;
  try {
    latestWorkspaceResponse = await getUserVar({
      host: generalEnv.userServiceUrl,
      userId: options.userId,
      key: USER_VARIABLE.LAST_OPENED_WORKSPACES,
    });
  } catch (err) {
    if (err instanceof AxiosError) {
      const { response } = err;
      if (response?.status === 404) {
        // no variable found on the server
        return null;
      } else {
        throw err;
      }
    } else {
      throw err;
    }
  }

  if (!latestWorkspaceResponse) {
    return null;
  }

  return (latestWorkspaceResponse.value as { [key: string]: string }) || null;
};

export const getLatestWorkspaceIdByUserVars = (options: GetLatestWorkspaceIdByUserVarsOptions): string | null => {
  const latestWorkspaceIdMapVar = options.userVars.find(({ key }) => key === USER_VARIABLE.LAST_OPENED_WORKSPACES);
  let latestWorkspaceIdMap = {} as { [key: string]: string };
  if (latestWorkspaceIdMapVar && latestWorkspaceIdMapVar.value) {
    latestWorkspaceIdMap = (latestWorkspaceIdMapVar.value as { [key: string]: string }) || null;
  }

  if (!latestWorkspaceIdMap) {
    return null;
  }

  if (typeof latestWorkspaceIdMap[options.orgId] === 'undefined') {
    return null;
  }

  return latestWorkspaceIdMap[options.orgId] || null;
};
